import { ChatLucaMsgModel, SavedChatHistoryModel } from 'countable@model';

export class AiMessagesUtil {
  static setNewChat(engId: number = 0, isRestricted: boolean = false) {
    const chatInfo: SavedChatHistoryModel = new SavedChatHistoryModel();
    chatInfo.engagementId = engId;
    chatInfo.chat_id = null;
    chatInfo.messages = [];
    const systemMsg: ChatLucaMsgModel = new ChatLucaMsgModel();
    systemMsg.showSpinner = false;
    systemMsg.messageType = 'SYSTEM_AI';
    systemMsg.lucaResponseType = 'MARKDOWN';
    systemMsg.lucaMarkdownResponse = engId > 0 ? (isRestricted ? AiResponses.restrictEngagement : AiResponses.engSelected) :
      AiResponses.selectEngagement;
    chatInfo.messages = [systemMsg];
    return chatInfo;
  }
}

export enum AiResponses {
  selectEngagement = `I'm Luca, your AI assistant here to help you navigate our platform and make your accounting tasks easier. To get started, please enter the name of the client or engagement ID in the search bar on the top that you wish to work on. I'll take it from there! If you have any questions or need assistance at any point, just let me know. I'm here to help you every step of the way.`,
  engSelected = `Great! I have the client name and engagement ID. If you have any questions about the engagement, feel free to ask. You can also use the # button below — or type # in the input — to choose from the available prompts and we’ll work through your requirements together.`,
  loadingContent = `Setting things up for you...`,
  glInput = `No problem, (User Name)! I just need few inputs from you to pull together the General Ledger Data.
Please select the year along with the criteria's. You can select multiple options.`,
  fileError = `Oops! It looks like you've exceeded the upload limit. You can upload up to 15 documents, with a total size of 25MB.
   Please adjust your files and give it another try.`,
  resError = `We encountered an issue while trying to process your request. please start a new chat for a different prompt. If the problem persists, contact support for further assistance. `,
  restrictEngagement = `As this engagement has been archived or completed, your access to the chat history is now limited to read-only,
  and starting new chats is restricted. Please reach out if you need further assistance.`,
  reconPlaid = 'Please select the trial balance account and its corresponding bank account to reconcile with the general ledger.',
  reconTb = 'Please select the trial balance account and upload a corresponding bank statement to reconcile with the general ledger.'
}

export const FORMULA_CONSTANTS = {
  depreciationFormula: `
Here is the content displayed normally:

---

#### Straight Line Depreciation Calculation

##### Formula for Annual Depreciation:
The annual depreciation is calculated as:
[
text{annual depreciation} = frac{text{ob cost} - text{residual value}}{text{useful life}}
]

##### Half-Year Rule Adjustment:
If the **half-year rule** applies, the annual depreciation is halved:
[
text{annual depreciation} = frac{text{annual depreciation}}{2}
]

##### Monthly Depreciation:
Monthly depreciation is derived by dividing the annual depreciation by 12:
[
text{monthly depreciation} = frac{text{annual depreciation}}{12}
]

##### Months in Use:
- **When an addition month is specified** (and the half-year rule does not apply):
  The number of months in use is calculated as:
  [
  text{months in use} = 12 - (text{addition month} - 1)
  ]

- **When a disposal month is specified** (and the half-year rule does not apply):
  The number of months in use is:
  [
  text{months in use} = text{disposal month} - 1
  ]

- **If the half-year rule applies:**
  The number of months in use is fixed at:
  [
  text{months in use} = 6
  ]

##### Total Depreciation:
The total depreciation is calculated as follows:
- **Without the half-year rule:**
  [
  text{total depreciation} = text{monthly depreciation} times text{months in use}
  ]

- **With the half-year rule:**
  [
  text{total depreciation} = text{annual depreciation}
  ]
`,
  declineBalanceFormula: `
#### Declining Balance Depreciation Calculation
##### Book Value:
The book value is calculated as:
[
text{book value} = text{ob cost} - text{residual value}
]
##### Annual Depreciation:
The annual depreciation is calculated using the declining balance method:
[
text{annual depreciation} = text{book value} times left( frac{text{rate}}{100} right)
]
##### Half-Year Rule Adjustment:
If the **half-year rule** applies, the annual depreciation is halved:
[
text{annual depreciation} = frac{text{annual depreciation}}{2}
]
##### Monthly Depreciation:
Monthly depreciation is calculated by dividing the annual depreciation by 12:
[
text{monthly depreciation} = frac{text{annual depreciation}}{12}
]
##### Months in Use:
- **When an addition month is specified** (and the half-year rule does not apply):
  The number of months in use is:
  [
  text{months in use} = 12 - (text{addition month} - 1)
  ]
- **When a disposal month is specified** (and the half-year rule does not apply):
  The number of months in use is:
  [
  text{months in use} = text{disposal month} - 1
  ]
- **If the half-year rule applies:**
  The number of months in use is:
  [
  text{months in use} = 6
  ]
- **Otherwise:**
  The number of months in use is:
  [
  text{months in use} = 12
  ]
##### Total Depreciation:
The total depreciation is calculated as:
- **Without the half-year rule:**
  [
  text{total depreciation} = text{monthly depreciation} times text{months in use}
  ]
- **With the half-year rule:**
  [
  text{total depreciation} = text{annual depreciation}
  ]
`,
  doubleDecliningFormula: `
#### Double Declining Depreciation Calculation
##### Depreciation Rate:
The depreciation rate is calculated as:
[
text{Depreciation Rate} = frac{2}{text{Useful Life}}
]
##### Book Value:
The book value is calculated as:
[
text{Book Value} = text{Original Cost} - text{Residual Value}
]
##### Annual Depreciation:
The annual depreciation is calculated as:
[
text{Annual Depreciation} = text{Book Value} times text{Depreciation Rate}
]
- **If the half-year rule applies:**
  [
  text{Annual Depreciation} = frac{text{Annual Depreciation}}{2}
  ]
##### Monthly Depreciation:
Monthly depreciation is calculated as:
[
text{Monthly Depreciation} = frac{text{Annual Depreciation}}{12}
]
##### Months in Use:
- **If an addition month is specified** (and the half-year rule does not apply):
  [
  text{Months in Use} = 12 - (text{Addition Month} - 1)
  ]
- **If a disposal month is specified** (and the half-year rule does not apply):
  [
  text{Months in Use} = text{Disposal Month} - 1
  ]
- **If the half-year rule applies:**
  [
  text{Months in Use} = 6
  ]
- **Otherwise:**
  [
  text{Months in Use} = 12
  ]
##### Total Depreciation:
The total depreciation is calculated as:
- **Without the half-year rule:**
  [
  text{Total Depreciation} = text{Monthly Depreciation} times text{Months in Use}
  ]
- **With the half-year rule:**
  [
  text{Total Depreciation} = text{Annual Depreciation}
  ]
`,
  sumOfYearsFormula: `
#### Sum of Years Depreciation Calculation
##### Sum of Years:
The sum of years is calculated as:
[
text{Sum of Years} = frac{text{Useful Life} times (text{Useful Life} + 1)}{2}
]
##### Annual Depreciation:
The annual depreciation is calculated as:
[
text{Annual Depreciation} = frac{text{Remaining Life}}{text{Sum of Years}} times (text{Original Cost} - text{Residual Value})
]
- **If the half-year rule applies:**
  [
  text{Annual Depreciation} = frac{text{Annual Depreciation}}{2}
  ]
##### Monthly Depreciation:
Monthly depreciation is calculated as:
[
text{Monthly Depreciation} = frac{text{Annual Depreciation}}{12}
]
##### Months in Use:
- **If an addition month is specified** (and the half-year rule does not apply):
  [
  text{Months in Use} = 12 - (text{Addition Month} - 1)
  ]
- **If a disposal month is specified** (and the half-year rule does not apply):
  [
  text{Months in Use} = text{Disposal Month} - 1
  ]
- **If the half-year rule applies:**
  [
  text{Months in Use} = 6
  ]
- **Otherwise:**
  [
  text{Months in Use} = 12
  ]
##### Total Depreciation:
The total depreciation is calculated as:
- **Without the half-year rule:**
  [
  text{Total Depreciation} = text{Monthly Depreciation} times text{Months in Use}
  ]
- **With the half-year rule:**
  [
  text{Total Depreciation} = text{Annual Depreciation}
  ]
`,
  unitsOfProductionFormula: `
#### Units of Production Depreciation Calculation
##### Depreciation per Unit:
Depreciation per unit is calculated as:
[
text{Depreciation per Unit} = frac{text{Original Cost} - text{Residual Value}}{text{Estimated Production}}
]
##### Annual Depreciation:
Annual depreciation is calculated as:
[
text{Annual Depreciation} = text{Depreciation per Unit} times text{Units Produced}
]
- **If the half-year rule applies:**
  [
  text{Annual Depreciation} = frac{text{Annual Depreciation}}{2}
  ]
##### Total Depreciation:
The total depreciation is equal to the annual depreciation.
[
text{Total Depreciation} = text{Annual Depreciation}
]
`,
};
